import { CaretRightOutlined, CheckCircleOutlined, CheckOutlined, CompressOutlined, DollarOutlined, ExpandAltOutlined, MinusCircleOutlined, PrinterOutlined, WarningOutlined, TableOutlined, FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import { Button, Col, Collapse, Empty, Row, Space, Statistic, Table, Tooltip, Divider, Card } from "antd";
import * as XLSX from "xlsx";
import { CompagniePanelHeader } from "./CompagniePanelHeader";
import { displayNumbers } from "../../../variables/numbersFunctions";
import dayjs from "dayjs";
import { annulationsColumns, emissionsColumns, recoursColumns, sinistresColumns } from "./RetrocessionData";
import { generateRetrocessionBordereauPDF } from "../../production/etatsProductionFunctions";

const { Panel } = Collapse;

export const BordereauParCompagnie = ({
    type,
    columns,
    activeKeys,
    setActiveKeys,
    processData,
    onValidate,
    isValidated,
    isDisabled,
    cessionsParCompagnie,
    compagniesRepartition = [],
    setActiveEmissionsKeys,
    setActiveAnnulationsKeys,
    setActiveSinistresKeys,
    setActiveRecoursKeys,
    loadingRetrocession
}) => {
    // Expand/Collapse All
    const expandAll = (type) => {
        const allKeys = cessionsParCompagnie.map(c => c.compagnie.code);
        switch (type) {
            case 'emissions':
                setActiveEmissionsKeys(allKeys);
                break;
            case 'annulations':
                setActiveAnnulationsKeys(allKeys);
                break;
            case 'sinistres':
                setActiveSinistresKeys(allKeys);
                break;
            case 'recours':
                setActiveRecoursKeys(allKeys);
                break;
        }
    };

    const collapseAll = (type) => {
        switch (type) {
            case 'emissions':
                setActiveEmissionsKeys([]);
                break;
            case 'annulations':
                setActiveAnnulationsKeys([]);
                break;
            case 'sinistres':
                setActiveSinistresKeys([]);
                break;
            case 'recours':
                setActiveRecoursKeys([]);
                break;
        }
    };

    const getTypeInfo = () => {
        switch (type) {
            case 'emissions':
                return {
                    title: 'Bordereau des Émissions Rétrocédées',
                    icon: <CheckCircleOutlined />,
                    color: '#52c41a',
                    bgColor: '#f6ffed',
                    description: 'Les polices dont l\'année d\'effet est ≤ l\'année de cession sont réparties selon les clés de répartition.'
                };
            case 'annulations':
                return {
                    title: 'Bordereau des Annulations Rétrocédées',
                    icon: <MinusCircleOutlined />,
                    color: '#faad14',
                    bgColor: '#fffbe6',
                    description: 'Toutes les annulations sont intégralement prises en compte et réparties.'
                };
            case 'sinistres':
                return {
                    title: 'Bordereau des Sinistres Payés Rétrocédés',
                    icon: <WarningOutlined />,
                    color: '#ff4d4f',
                    bgColor: '#fff2f0',
                    description: 'Répartition selon les clés basées sur la date de survenance du sinistre.'
                };
            case 'recours':
                return {
                    title: 'Bordereau des Recours Encaissés Rétrocédés',
                    icon: <DollarOutlined />,
                    color: '#13c2c2',
                    bgColor: '#e6fffb',
                    description: 'Répartition selon les clés basées sur la date de survenance du sinistre lié.'
                };
            default:
                return {};
        }
    };

    const typeInfo = getTypeInfo();

    // Calculer les totaux globaux
    let totalGlobalOriginal = 0;
    let totalGlobalRetrocede = 0;
    let totalGlobalItems = 0;

    // Totaux par compagnie pour le résumé
    const totauxParCompagnie = {};
    compagniesRepartition.forEach(comp => {
        totauxParCompagnie[comp.code] = { taux: 0, montant: 0, count: 0 };
    });

    // Collecter toutes les données pour le tableau global
    const allData = [];

    cessionsParCompagnie.forEach(compagnie => {
        const { data, totalOriginal, totalRetrocede } = processData(compagnie);
        totalGlobalOriginal += totalOriginal;
        totalGlobalRetrocede += totalRetrocede;
        totalGlobalItems += data.length;

        // Ajouter les données avec la compagnie cédante
        data.forEach((item, index) => {
            allData.push({
                ...item,
                compagnieCedante: compagnie.compagnie,
                uniqueKey: `${compagnie.compagnie.code}-${item.codeId || index}`
            });

            // Calculer les totaux par compagnie bénéficiaire
            item.repartition?.forEach(rep => {
                if (totauxParCompagnie[rep.compagnieCode]) {
                    totauxParCompagnie[rep.compagnieCode].montant += rep.montant || 0;
                    totauxParCompagnie[rep.compagnieCode].count += 1;
                    totauxParCompagnie[rep.compagnieCode].taux = rep.taux;
                }
            });
        });
    });

    // ── Exports du bordereau CONSOLIDÉ (remplace window.print) ──────────────────
    const buildExportRows = () => allData.map((item) => ({
        numeroPolice: item.numeroPolice || item.numeroPoliceP || item.numeroPoliceM || "",
        cedante: item.compagnieCedante?.raisonSociale || item.compagnieCedante?.code || "",
        echeance: (item.echeance && item.echeance !== "N/A") ? dayjs(item.echeance).format("DD/MM/YYYY") : "",
        // Prime cédée = prime nette = Total Rétrocédé (assiette nette intégralement rétrocédée).
        primeCedee: item.totalRetrocede || 0,
        totalRetrocede: item.totalRetrocede || 0,
        // Par compagnie bénéficiaire : { taux (%), montant } — comme l'affichage web (Taux + Montant).
        parCompagnie: Object.fromEntries((item.repartition || []).map((r) => [r.compagnieCode, { taux: r.taux || 0, montant: r.montant || 0 }])),
    }));

    const handleExportExcel = () => {
        const rows = buildExportRows();
        const compName = (c) => c.raisonSociale || c.compagnieNom || c.code;
        // Deux colonnes par compagnie : « … (Taux %) » puis « … (Rétrocédé) », comme sur le web.
        const compHeaders = compagniesRepartition.flatMap((c) => [`${compName(c)} (Taux %)`, `${compName(c)} (Rétrocédé)`]);
        const header = ["N° Police", "Cédante", "Échéance", "Prime cédée", ...compHeaders, "Total Rétrocédé"];
        const aoa = [
            [`${typeInfo.title} — Vue consolidée`],
            [`${allData.length} élément(s) de ${cessionsParCompagnie.length} compagnie(s) cédante(s)`],
            [], header,
        ];
        const cell = (r, c) => r.parCompagnie[c.code] || { taux: 0, montant: 0 };
        rows.forEach((r) => aoa.push([
            r.numeroPolice, r.cedante, r.echeance, Math.trunc(r.primeCedee),
            ...compagniesRepartition.flatMap((c) => {
                const pc = cell(r, c);
                return [Number((Number(pc.taux) || 0).toFixed(2)), Math.trunc(pc.montant || 0)];
            }),
            Math.trunc(r.totalRetrocede),
        ]));
        const som = (f) => rows.reduce((s, r) => s + (Number(f(r)) || 0), 0);
        aoa.push(["TOTAL", "", "", Math.trunc(som((r) => r.primeCedee)),
            // Ligne TOTAL : colonne Taux laissée vide (non sommable), colonne Rétrocédé sommée.
            ...compagniesRepartition.flatMap((c) => ["", Math.trunc(som((r) => cell(r, c).montant || 0))]),
            Math.trunc(totalGlobalRetrocede)]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aoa), "Rétrocession");
        XLSX.writeFile(wb, `Retrocession_${type}_consolide.xlsx`);
    };

    const handleExportPdf = async () => {
        const uri = await generateRetrocessionBordereauPDF({
            titre: `${typeInfo.title} — VUE CONSOLIDÉE`,
            sousTitre: `${allData.length} élément(s) de ${cessionsParCompagnie.length} compagnie(s) cédante(s)`,
            compagnies: compagniesRepartition,
            rows: buildExportRows(),
            totalRetrocede: totalGlobalRetrocede,
        });
        const a = document.createElement("a");
        a.href = uri; a.download = `Retrocession_${type}_consolide.pdf`; a.click();
    };

    // Fonction pour générer le summary du tableau avec totaux par compagnie
    const generateTableSummary = (data, compagnie) => {
        const totaux = {};
        compagniesRepartition.forEach(comp => {
            totaux[comp.code] = 0;
        });

        let totalOriginal = 0;
        let totalRetrocede = 0;

        data.forEach(item => {
            totalOriginal += (item.primeNette != null ? item.primeNette : (item.primeACeder || item.montant)) || 0;
            totalRetrocede += item.totalRetrocede || 0;

            item.repartition?.forEach(rep => {
                if (totaux[rep.compagnieCode] !== undefined) {
                    totaux[rep.compagnieCode] += rep.montant || 0;
                }
            });
        });

        const baseColCount = type === 'emissions' ? 5 : type === 'annulations' ? 4 : type === 'sinistres' ? 6 : 5;

        return (
            <Table.Summary fixed>
                <Table.Summary.Row className="summary-row">
                    <Table.Summary.Cell index={0} colSpan={baseColCount}>
                        <strong>TOTAL {compagnie.compagnie.raisonSociale}</strong>
                    </Table.Summary.Cell>
                    {compagniesRepartition.map((comp, idx) => (
                        <Table.Summary.Cell
                            key={`summary-${comp.code}`}
                            index={baseColCount + idx}
                            colSpan={2}
                            align="right"
                        >
                            <strong style={{ color: typeInfo.color }}>
                                {displayNumbers(totaux[comp.code])} F
                            </strong>
                        </Table.Summary.Cell>
                    ))}
                    <Table.Summary.Cell index={baseColCount + compagniesRepartition.length} align="right">
                        <strong style={{ color: '#722ed1', fontSize: 14 }}>
                            {displayNumbers(totalRetrocede)} F
                        </strong>
                    </Table.Summary.Cell>
                </Table.Summary.Row>
            </Table.Summary>
        );
    };

    // Fonction pour générer le summary du tableau GLOBAL
    const generateGlobalTableSummary = (data) => {
        const totaux = {};
        compagniesRepartition.forEach(comp => {
            totaux[comp.code] = 0;
        });

        let totalOriginal = 0;
        let totalRetrocede = 0;

        data.forEach(item => {
            totalOriginal += Math.abs((item.primeNette != null ? item.primeNette : (item.primeACeder || item.montant)) || 0);
            totalRetrocede += Math.abs(item.totalRetrocede || 0);

            item.repartition?.forEach(rep => {
                if (totaux[rep.compagnieCode] !== undefined) {
                    totaux[rep.compagnieCode] += Math.abs(rep.montant || 0);
                }
            });
        });

        const baseColCount = type === 'emissions' ? 5 : type === 'annulations' ? 4 : type === 'sinistres' ? 6 : 5;

        return (
            <Table.Summary fixed>
                <Table.Summary.Row className="summary-row" style={{ background: '#f0f5ff' }}>
                    <Table.Summary.Cell index={0} colSpan={baseColCount}>
                        <strong style={{ fontSize: 14, color: '#1a365d' }}>
                            📊 TOTAL GÉNÉRAL ({data.length} éléments)
                        </strong>
                    </Table.Summary.Cell>
                    {compagniesRepartition.map((comp, idx) => (
                        <Table.Summary.Cell
                            key={`global-summary-${comp.code}`}
                            index={baseColCount + idx}
                            colSpan={2}
                            align="right"
                        >
                            <strong style={{ color: typeInfo.color, fontSize: 13 }}>
                                {displayNumbers(totaux[comp.code])} F
                            </strong>
                        </Table.Summary.Cell>
                    ))}
                    <Table.Summary.Cell index={baseColCount + compagniesRepartition.length} align="right">
                        <strong style={{ color: '#722ed1', fontSize: 16 }}>
                            {displayNumbers(totalRetrocede)} F
                        </strong>
                    </Table.Summary.Cell>
                </Table.Summary.Row>
            </Table.Summary>
        );
    };

    // Colonnes pour le tableau global (avec colonne compagnie cédante en plus)
    const getGlobalColumns = () => {
        switch (type) {
            case 'emissions':
                return emissionsColumns(compagniesRepartition, true);
            case 'annulations':
                return annulationsColumns(compagniesRepartition, true);
            case 'sinistres':
                return sinistresColumns(compagniesRepartition, true);
            case 'recours':
                return recoursColumns(compagniesRepartition, true);
            default:
                return columns;
        }
    };

    const globalColumns = getGlobalColumns().map(col => {
        if (col.fixed === 'left' && col.key !== 'totalRetrocede') {
            return {
                ...col,
                filters: cessionsParCompagnie.map(c => ({
                    text: `${c.compagnie.raisonSociale} (${c.compagnie.code})`,
                    value: c.compagnie.code
                })),
                onFilter: (value, record) => record.compagnieCedante?.code === value,
                filterMultiple: true,
            };
        }
        return col;
    });

    return (
        <div className="bordereau-container">

            {/* Liste des compagnies */}
            {cessionsParCompagnie.length === 0 ? (
                <Empty
                    description="Aucune donnée disponible pour cette période"
                    style={{ padding: '60px 0' }}
                />
            ) : (
                <>
                    <Collapse
                        activeKey={activeKeys}
                        onChange={setActiveKeys}
                        expandIcon={({ isActive }) => (
                            <CaretRightOutlined
                                rotate={isActive ? 90 : 0}
                                style={{ fontSize: 14 }}
                            />
                        )}
                        className="compagnie-collapse"
                    >
                        {cessionsParCompagnie.map(compagnie => {
                            const { data, totalOriginal, totalRetrocede } = processData(compagnie);

                            return (
                                <Panel
                                    key={compagnie.compagnie.code}
                                    header={
                                        <CompagniePanelHeader
                                            compagnie={compagnie.compagnie}
                                            totalItems={data.length}
                                            totalOriginal={totalOriginal}
                                            totalRetrocede={totalRetrocede}
                                            type={type}
                                        />
                                    }
                                    className="compagnie-panel"
                                >
                                    <Table
                                        columns={columns}
                                        dataSource={data}
                                        rowKey={(record, index) => `${compagnie.compagnie.code}-${record.codeId || index}`}
                                        pagination={{
                                            pageSize: 10,
                                            showSizeChanger: true,
                                            showTotal: (total) => `${total} élément(s)`,
                                            size: 'small'
                                        }}
                                        size="small"
                                        scroll={{ x: 610 + (compagniesRepartition.length * 158) }}
                                        bordered
                                        summary={() => generateTableSummary(data, compagnie)}
                                    />
                                </Panel>
                            );
                        })}
                    </Collapse>

                    {/* TABLEAU RÉCAPITULATIF GLOBAL */}
                    <Divider style={{ margin: '32px 0 24px' }}>
                        <Space>
                            <TableOutlined />
                            <span style={{ fontWeight: 600, fontSize: 14 }}>
                                Bordereau Consolidé - Toutes Compagnies
                            </span>
                        </Space>
                    </Divider>

                    <Card
                        className="global-bordereau-card"
                        style={{
                            borderRadius: 12,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            border: `2px solid ${typeInfo.color}`,
                            marginBottom: 24
                        }}
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{
                                    color: typeInfo.color,
                                    fontSize: 20
                                }}>
                                    {typeInfo.icon}
                                </span>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 16, color: '#1a365d' }}>
                                        {typeInfo.title} - Vue Consolidée
                                    </div>
                                    <div style={{ fontSize: 12, color: '#666', fontWeight: 400 }}>
                                        {allData.length} éléments de {cessionsParCompagnie.length} compagnie(s) cédante(s)
                                    </div>
                                </div>
                            </div>
                        }
                        extra={
                            <Space>
                                <Button icon={<FileExcelOutlined />} onClick={handleExportExcel}
                                    style={{ borderColor: "#059669", color: "#059669" }}>
                                    Excel
                                </Button>
                                <Button icon={<FilePdfOutlined />} onClick={handleExportPdf}
                                    style={{ borderColor: "#dc2626", color: "#dc2626" }}>
                                    PDF
                                </Button>
                            </Space>
                        }
                    >


                        {/* Tableau consolidé */}
                        <Table
                            columns={getGlobalColumns()}
                            dataSource={allData}
                            rowKey="uniqueKey"
                            pagination={{
                                pageSize: 20,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                showTotal: (total, range) => `${range[0]}-${range[1]} sur ${total} éléments`,
                                showQuickJumper: true
                            }}
                            size="small"
                            scroll={{ x: 610 + (compagniesRepartition.length * 158), y: 500 }}
                            bordered
                            summary={() => generateGlobalTableSummary(allData)}
                            rowClassName={(record, index) => index % 2 === 0 ? 'even-row' : 'odd-row'}
                        />

                        {/* Résumé par compagnie bénéficiaire */}
                        <div style={{
                            background: typeInfo.bgColor,
                            borderRadius: 8,
                            padding: 16,
                            marginBottom: 16
                        }}>
                            <div style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: '#666',
                                marginBottom: 12
                            }}>
                                💰 TOTAUX PAR COMPAGNIE
                            </div>
                            <Row gutter={[16, 16]}>
                                {compagniesRepartition.map(comp => (
                                    <Col
                                        key={comp.code}
                                        xs={24}
                                        sm={12}
                                        md={8}
                                        lg={Math.max(4, Math.floor(24 / Math.min(compagniesRepartition.length, 6)))}
                                    >
                                        <div style={{
                                            background: 'white',
                                            borderRadius: 8,
                                            padding: 16,
                                            textAlign: 'center',
                                            border: `1px solid ${typeInfo.color}30`,
                                            height: '100%'
                                        }}>
                                            <div style={{
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#1a365d',
                                                marginBottom: 4
                                            }}>
                                                {comp.raisonSociale}
                                            </div>
                                            <div style={{
                                                fontSize: 11,
                                                color: '#888',
                                                marginBottom: 8
                                            }}>
                                                Code: {comp.code}
                                            </div>
                                            <div style={{
                                                fontSize: 18,
                                                fontWeight: 700,
                                                color: typeInfo.color
                                            }}>
                                                {displayNumbers(totauxParCompagnie[comp.code]?.montant || 0)} F
                                            </div>
                                            <div style={{
                                                fontSize: 10,
                                                color: '#999',
                                                marginTop: 4
                                            }}>
                                                {totauxParCompagnie[comp.code]?.count || 0} opération(s)
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            {/* Total global */}
                            <div style={{
                                marginTop: 16,
                                padding: 16,
                                background: `linear-gradient(135deg, ${typeInfo.color}20 0%, ${typeInfo.color}40 100%)`,
                                borderRadius: 8,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <div style={{ fontWeight: 600, color: '#1a365d' }}>
                                    TOTAL GÉNÉRAL RÉTROCÉDÉ
                                </div>
                                <div style={{
                                    fontSize: 24,
                                    fontWeight: 700,
                                    color: typeInfo.color
                                }}>
                                    {displayNumbers(Object.values(totauxParCompagnie).reduce((sum, v) => sum + v.montant, 0))} F CFA
                                </div>
                            </div>
                        </div>
                    </Card>
                </>
            )}

            {/* Footer avec bouton de validation */}
            <div className="bordereau-footer">
                <Button
                    type="primary"
                    size="large"
                    icon={isValidated ? <CheckOutlined /> : null}
                    onClick={onValidate}
                    disabled={isValidated || isDisabled || cessionsParCompagnie.length === 0}
                    loading={loadingRetrocession}
                    style={{
                        backgroundColor: isValidated ? '#52c41a' : typeInfo.color,
                        borderColor: isValidated ? '#52c41a' : typeInfo.color
                    }}
                >
                    {isValidated ? 'Bordereau validé' : 'Valider le bordereau'}
                </Button>
            </div>
        </div>
    );
};